/**
 * Public-facing prices for marketing and JSON-LD. Set in env so they match Stripe
 * without redeploying when you only change Stripe Dashboard copy.
 * NEXT_PUBLIC_PRICE_SOLO, NEXT_PUBLIC_PRICE_PRO, NEXT_PUBLIC_PRICE_AGENCY (USD integers).
 */
function parseTier(key: "STARTER" | "SOLO" | "PRO" | "AGENCY", fallback: number): number {
  const raw = process.env[`NEXT_PUBLIC_PRICE_${key}`];
  if (raw == null || raw === "") return fallback;
  const n = Number(raw);
  return Number.isFinite(n) ? n : fallback;
}

export function getPublicPricing() {
  return {
    starter: parseTier("STARTER", 19),
    solo: parseTier("SOLO", 39),
    pro: parseTier("PRO", 99),
    agency: parseTier("AGENCY", 199),
  };
}

/**
 * Annual billing is presented as "2 months free" — i.e. you pay for 10 months
 * and get 12. That's a clean ~16.7% discount and has the marketing virtue of
 * being a concrete benefit instead of a percentage. It also means the per-
 * month-equivalent at annual matches the monthly price exactly when amortized
 * over 12 months: $39/mo monthly billing vs. $390/yr ($32.50/mo) annual.
 *
 * Stripe price IDs for the annual products live in env var
 * `STRIPE_PRICE_*_ANNUAL`. The numbers below are display-only.
 */
export const ANNUAL_MONTHS_BILLED = 10;
export const ANNUAL_MONTHS_DELIVERED = 12;

/**
 * Annual price in USD for each tier, rounded to whole dollars.
 * 39 * 10 = 390; 99 * 10 = 990; 199 * 10 = 1990.
 */
export function getPublicAnnualPricing() {
  const monthly = getPublicPricing();
  return {
    starter: monthly.starter * ANNUAL_MONTHS_BILLED,
    solo: monthly.solo * ANNUAL_MONTHS_BILLED,
    pro: monthly.pro * ANNUAL_MONTHS_BILLED,
    agency: monthly.agency * ANNUAL_MONTHS_BILLED,
  };
}

/**
 * "$X.XX / mo when billed annually" display number. Drops fractional cents
 * if the math is exact ($32.50, not $32.5000).
 */
export function getPublicAnnualMonthlyEquivalent() {
  const annual = getPublicAnnualPricing();
  const div = (n: number) => Math.round((n / ANNUAL_MONTHS_DELIVERED) * 100) / 100;
  return {
    starter: div(annual.starter),
    solo: div(annual.solo),
    pro: div(annual.pro),
    agency: div(annual.agency),
  };
}

export function formatPriceUsdMonthly(amount: number): string {
  return `$${amount}`;
}

export type PlanIntroDisplay = {
  days: number;
  amountUsd: number;
  /** Primary line on plan cards, e.g. "Free" or "$5" */
  introPrice: string;
  /** Secondary line, e.g. "for your first 3 days" */
  introCaption: string;
  monthlyAfter: string;
};

/** Matches billing page / Stripe checkout intro offer copy. */
export function getPlanIntroDisplay(
  plan: "starter" | "solo" | "pro" | "agency",
  monthlyUsd: number,
  opts: { trialDays: number; introAmountUsd: number }
): PlanIntroDisplay {
  const days = opts.trialDays;
  const amountUsd = opts.introAmountUsd;
  const monthlyAfter = `${formatPriceUsdMonthly(monthlyUsd)}/mo`;

  if (plan === "starter" || plan === "solo" || amountUsd === 0) {
    return {
      days,
      amountUsd: 0,
      introPrice: "Free",
      introCaption: `for your first ${days} days`,
      monthlyAfter,
    };
  }

  return {
    days,
    amountUsd,
    introPrice: formatPriceUsdMonthly(amountUsd),
    introCaption: `for your first ${days} days`,
    monthlyAfter,
  };
}

/** Entry-tier price for schema.org / OG-style snippets — the "starting at" number. */
export function getDefaultOfferPrice(): string {
  return String(getPublicPricing().starter);
}
